/**
 * « Mes notes et évals » (`/student/evaluations`, migration 0321) : moyenne
 * pondérée (générale + par matière), évals à noter, évals à venir avec les
 * SKILLCARDS à réviser, notes reçues. Tout est saisi par l'élève.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, CalendarClock, CheckCircle2, ChevronRight, Plus } from 'lucide-react';
import { Button, MathText } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { useMyEvaluations } from '@/lib/evaluations/api';
import {
  averagesBySubject,
  bucketEvaluations,
  cardsToReview,
  countdownLabel,
  evaluationDateLabel,
  formatGradeNumber,
  formatScore,
  weightedAverage,
  type StudentEvaluation,
} from '@/lib/evaluations/grades';
import { EvaluationFormSheet, GradeSheet } from './evaluation-form';
import { useEvaluationContext } from './use-evaluation-context';

export function StudentEvaluationsPage() {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const evals = useMyEvaluations(userId);
  const ctx = useEvaluationContext(userId);
  const [editing, setEditing] = useState<StudentEvaluation | 'new' | 'new-graded' | null>(null);
  const [grading, setGrading] = useState<StudentEvaluation | null>(null);

  const list = evals.data ?? [];
  const { upcoming, toGrade, graded } = bucketEvaluations(list);
  const average = weightedAverage(graded);
  const bySubject = averagesBySubject(graded);

  return (
    <div className="mx-auto max-w-lg space-y-6 px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to="/student/dashboard"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Accueil
      </Link>
      <div className="space-y-3">
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">Mes notes et évals</h1>
        <p className="text-ink-600 text-sm">
          Programme tes contrôles, indique sur quelles SKILLCARDS ils portent, puis note-les : ta
          moyenne se calcule toute seule.
        </p>
        {ctx.tutorNames.length > 0 && (
          <p className="text-ink-500 text-xs">
            Partagé avec {ctx.tutorNames.join(', ')} : ton prof voit tes évals et tes notes.
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button fullWidth className="sm:w-auto" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" aria-hidden /> Programmer une éval
          </Button>
          <Button
            variant="outline"
            fullWidth
            className="sm:w-auto"
            onClick={() => setEditing('new-graded')}
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden /> Ajouter une note reçue
          </Button>
        </div>
      </div>

      {evals.isLoading ? (
        <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
      ) : list.length === 0 ? (
        <p className="border-ink-100 text-ink-500 rounded-2xl border border-dashed p-6 text-center text-sm">
          Aucune éval pour l’instant. Ajoute ton prochain contrôle, ou une note déjà reçue.
        </p>
      ) : (
        <>
          <section
            aria-label="Ma moyenne"
            className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 rounded-2xl border p-4"
          >
            <p className="text-ink-500 text-xs font-bold uppercase tracking-wider">Ma moyenne</p>
            <p className="font-display text-ink-900 text-4xl font-extrabold">
              {formatGradeNumber(average)}
              <span className="text-ink-500 text-lg font-bold"> / 20</span>
            </p>
            <p className="text-ink-500 text-sm">
              {graded.length === 0
                ? 'Pas encore de note.'
                : `${graded.length} note${graded.length > 1 ? 's' : ''}, coefficients compris`}
            </p>
            {bySubject.length > 1 && (
              <ul className="mt-3 space-y-1">
                {bySubject.map((s) => (
                  <li key={s.subject} className="flex justify-between gap-3 text-sm">
                    <span className="text-ink-700 truncate">{s.subject}</span>
                    <span className="text-ink-900 font-semibold">
                      {formatGradeNumber(s.average)} / 20
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {toGrade.length > 0 && (
            <section className="space-y-2">
              <h2 className="font-display text-ink-900 text-lg font-bold">
                À noter ({toGrade.length})
              </h2>
              {toGrade.map((e) => (
                <div
                  key={e.id}
                  className="border-ink-100 ocean:bg-white/5 flex flex-col gap-2 rounded-2xl border bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <button type="button" onClick={() => setEditing(e)} className="min-w-0 text-left">
                    <p className="text-ink-900 font-semibold">
                      <MathText>{e.title}</MathText>
                    </p>
                    <p className="text-ink-500 text-sm">
                      {evaluationDateLabel(e.eval_date)}
                      {e.subject ? ` · ${e.subject}` : ''}
                    </p>
                  </button>
                  <Button className="w-full sm:w-auto" onClick={() => setGrading(e)}>
                    Saisir ma note
                  </Button>
                </div>
              ))}
            </section>
          )}

          {upcoming.length > 0 && (
            <section className="space-y-2">
              <h2 className="font-display text-ink-900 text-lg font-bold">À venir</h2>
              {upcoming.map((e) => (
                <UpcomingEvaluation
                  key={e.id}
                  evaluation={e}
                  masteryById={ctx.masteryById}
                  onEdit={() => setEditing(e)}
                />
              ))}
            </section>
          )}

          {graded.length > 0 && (
            <section className="space-y-2">
              <h2 className="font-display text-ink-900 text-lg font-bold">Mes notes</h2>
              <ul className="space-y-2">
                {graded.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => setEditing(e)}
                      className="border-ink-100 ocean:bg-white/5 flex min-h-[2.75rem] w-full items-center gap-3 rounded-2xl border bg-white p-3 text-left active:scale-[0.99]"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="text-ink-900 block truncate font-semibold">
                          <MathText>{e.title}</MathText>
                        </span>
                        <span className="text-ink-500 block truncate text-sm">
                          {evaluationDateLabel(e.eval_date)}
                          {e.subject ? ` · ${e.subject}` : ''}
                          {e.coefficient !== 1 ? ` · coef ${formatGradeNumber(e.coefficient)}` : ''}
                        </span>
                      </span>
                      <span className="text-ink-900 shrink-0 font-bold">{formatScore(e)}</span>
                      <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {editing && (
        <EvaluationFormSheet
          userId={userId}
          evaluation={editing === 'new' || editing === 'new-graded' ? null : editing}
          mode={editing === 'new-graded' ? 'graded' : 'plan'}
          candidates={ctx.candidates}
          subjectSuggestions={ctx.subjectSuggestions}
          onClose={() => setEditing(null)}
          hasTutor={ctx.tutorNames.length > 0}
        />
      )}
      {grading && (
        <GradeSheet
          userId={userId}
          evaluation={grading}
          onClose={() => setGrading(null)}
          hasTutor={ctx.tutorNames.length > 0}
        />
      )}
    </div>
  );
}

/** Une éval à venir : compte à rebours + SKILLCARDS à réviser (la plus fragile d'abord). */
export function UpcomingEvaluation({
  evaluation,
  masteryById,
  onEdit,
  maxCards,
}: {
  evaluation: StudentEvaluation;
  masteryById: ReadonlyMap<string, number>;
  onEdit?: () => void;
  /** Limite le nombre de cartes affichées (tableau de bord). */
  maxCards?: number;
}) {
  const review = cardsToReview(evaluation.cards, masteryById);
  const shown = maxCards ? review.slice(0, maxCards) : review;
  const header = (
    <>
      <span className="bg-primary-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white">
        <CalendarClock className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 block font-semibold">
          <MathText>{evaluation.title}</MathText>
        </span>
        <span className="text-ink-500 block text-sm">
          <span className="text-primary-700 ocean:text-primary-200 font-semibold">
            {countdownLabel(evaluation.eval_date)}
          </span>{' '}
          · {evaluationDateLabel(evaluation.eval_date)}
          {evaluation.subject ? ` · ${evaluation.subject}` : ''}
        </span>
      </span>
    </>
  );
  return (
    <div className="border-ink-100 ocean:bg-white/5 space-y-2 rounded-2xl border bg-white p-3">
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="flex w-full items-start gap-3 text-left active:scale-[0.99]"
        >
          {header}
        </button>
      ) : (
        <div className="flex items-start gap-3">{header}</div>
      )}
      {shown.length > 0 && (
        <div className="space-y-1">
          <p className="text-ink-500 text-xs font-bold uppercase tracking-wider">À réviser</p>
          <ul className="flex flex-wrap gap-1.5">
            {shown.map((c) => (
              <li key={c.card_id}>
                <Link
                  to="/card/$cardId"
                  params={{ cardId: c.card_id }}
                  className="border-ink-200 text-ink-800 inline-flex min-h-[2.75rem] max-w-full items-center gap-1.5 rounded-full border px-3 text-sm active:scale-[0.98]"
                >
                  <span className="truncate">
                    <MathText>{c.card_title}</MathText>
                  </span>
                  <span className="text-ink-500 shrink-0 text-xs">
                    {c.mastery == null ? 'à découvrir' : `${c.mastery} %`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {review.length > shown.length && (
            <p className="text-ink-500 text-xs">+ {review.length - shown.length} autres</p>
          )}
        </div>
      )}
    </div>
  );
}
